import ApiService from "@/api/ApiService";
import { useGlobalStore, usePersistantStore } from "@/stores/global";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { CalJob } from "../calendarElements/InnerCalendar";
import { MaterialOrder } from "@/components/planning/Project";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import OrderInformation from "./OrderInformation";
import { useNavigate } from "react-router-dom";
import useRights from "@/shared/api/useRights";
import OrderHistoryDialog from "../dialogs/OrderHistoryDialog";
import OrderLocationDialog from "../dialogs/OrderLocationDialog";

interface ResourceTag {
  id: number;
  name: string;
  type: "HUMAN" | "RESOURCE" | "CREW";
  calendarType: "NONE" | "CALENDAR_ONLY" | "WISHABLE" | "REQUESTABLE";
}

export interface StaffMember {
  id: number;
  name: string;
  tags: ResourceTag[];
}

export interface Crew {
  id: number;
  name: string;
}

export interface Resource {
  id: number;
  name: string;
  tags: ResourceTag[];
  number: string;
}

interface ResourceBooking {
  id: number;
  crew: Crew | null;
  isLeader: boolean;
  resource: Resource | null;
  staffMember: StaffMember | null;
  order: ResourceOrder;
  type: "CREW" | "RESOURCE" | "HUMAN";
}

interface ResourceOrder {
  id: number;
  amount: number;
  tag: ResourceTag | null;
  comment: string;
  type: "CREW" | "RESOURCE" | "HUMAN";
  job: number | null;
  phase: number | null;
  deleted: boolean;
  references: (number | null)[];
  exceptions: string[];
}

export interface WithOrders {
  materialOrders: MaterialOrder[];
  resourceOrders: {
    resourceBookings: ResourceBooking[];
    resourceOrders: ResourceOrder[];
  };
}

const dispoCalJobIdsQueryKey = (date: string, region: string | number) => ["dispoCalJobIds", date, "" + region] as const;

export const dispoCalJobQueryKey = (jobId: number) => ["dispoCalJob", "" + jobId] as const;

const nameSorter = (a, b) => a.phase.project.name.localeCompare(b.phase.project.name);
const numberSorter = (a, b) => a.phase.project.number.localeCompare(b.phase.project.number);

const dispoStatusSorting = {
  NONE: "c",
  EMPTY: "d",
  DONE: "e",
  UNSENT: "b",
  NONEED: "f",
};

const dispoStatusColor = {
  NONE: "yellow",
  EMPTY: "blue",
  DONE: "darkgreen",
  UNSENT: "green",
  NONEED: "darkgreen",
};

const dispoStatusText = {
  NONE: "Vorplanung vorhanden, (noch) nicht bestellt",
  EMPTY: "Nichts geplant oder bestellt",
  DONE: "Alles erledigt",
  UNSENT: "Bestellt, Transporte noch nicht übertragen",
  NONEED: "Wunschlos glücklich",
};

const findStatus = (a: CalJob & WithOrders) => ({
  ...a,
  dispoStatus: !a.orderSentAt
    ? a.materialOrders.some((x) => !x.deleted || x.transportStatus === "STORNO")
      ? "NONE"
      : "EMPTY"
    : a.materialOrders.some((x) => !x.deleted || x.transportStatus === "STORNO")
      ? a.materialOrders.every(
          (x) =>
            x.transportStatus === "ACCEPTED" ||
            x.transportStatus === "DELETED" ||
            x.transportStatus === "REJECTED" ||
            x.transportStatus === "RETRACTED" ||
            ((x.article?.bauhof ?? 0) !== 0 && x.transportInformation.length === 0),
        )
        ? "DONE"
        : "UNSENT"
      : "NONEED",
});

const statusSorter = (a, b) =>
  dispoStatusSorting[a.dispoStatus].localeCompare(dispoStatusSorting[b.dispoStatus]) || a.phase.project.name.localeCompare(b.phase.project.name);

export default forwardRef(function CalendarListView({ setCalendar }: {}, ref) {
  const rights = useRights();
  const settings = usePersistantStore((state) => state.getSettings());
  const region = usePersistantStore((state) => state.region);
  const queryClient = useQueryClient();
  const setSettings = usePersistantStore((state) => state.setSettings);
  const userId = usePersistantStore((state) => state.userId);
  const [visibilityOverrides, setVisibilityOverrides] = useState<Set<number>>(new Set());

  // Individual job toggles remain local and must not carry over to another user.
  useEffect(() => setVisibilityOverrides(new Set()), [userId]);

  const date = useGlobalStore((state) => state.date);

  const jobIdsQuery = useQuery<number[], AxiosResponse>({
    queryKey: dispoCalJobIdsQueryKey(date, region),
    queryFn: ApiService.getDispoCalJobIds(date, region),
  });

  const jobsQuery = useQueries({
    queries: (jobIdsQuery.data ?? []).map((jobId) => ({
      queryKey: dispoCalJobQueryKey(jobId),
      queryFn: ApiService.getDispoCalJobById(jobId),
      enabled: !!jobIdsQuery.data,
    })),
    combine: (results) => ({
      data: results.map((result) => result.data).filter(Boolean) as (CalJob & WithOrders)[],
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    }),
  });

  const [modalOpen, setModalOpen] = useState(null);

  const navigate = useNavigate();

  const data = useMemo(() => {
    const sorter = settings.sortDispoBy === "NAME" ? nameSorter : settings.sortDispoBy === "NUMBER" ? numberSorter : statusSorter;

    return jobsQuery.data.map(findStatus).sort(sorter);
  }, [jobsQuery.data, settings.sortDispoBy]);

  const isJobVisible = (jobId: number) => {
    const isOverridden = visibilityOverrides.has(jobId);

    if (settings.dispoExpanded) {
      return !isOverridden;
    }

    return isOverridden;
  };

  const toggleJobVisibility = (jobId: number) => {
    setVisibilityOverrides((prevSet) => {
      const newSet = new Set(prevSet);

      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }

      return newSet;
    });
  };

  useImperativeHandle(ref, () => ({
    hideAll: () => {
      setSettings({ dispoExpanded: false });
      setVisibilityOverrides(new Set());
    },
    showAll: () => {
      setSettings({ dispoExpanded: true });
      setVisibilityOverrides(new Set());
    },
  }));

  const isLoading = jobIdsQuery.isLoading || jobsQuery.isLoading;
  const isLoadingError = jobIdsQuery.isLoadingError || jobsQuery.isError;

  return (
    <div className="calListWrapper">
      <OrderHistoryDialog orderId={!!modalOpen && modalOpen[0] === "HISTORY" ? modalOpen[1] : 0} close={() => setModalOpen(null)} />
      <OrderLocationDialog orderId={!!modalOpen && modalOpen[0] === "LOCATION" ? modalOpen[1] : 0} close={() => setModalOpen(null)} />
      {isLoading ? (
        <CircularProgress />
      ) : isLoadingError ? (
        "Error"
      ) : (
        data.map((x) => {
          const isVisible = isJobVisible(x.id);

          return (
            <Fragment key={x.id}>
              <div className="calJobHeader" onClick={() => toggleJobVisibility(x.id)}>
                <Tooltip placement="right" title={dispoStatusText[x.dispoStatus]}>
                  <div className={"calOrderState color " + dispoStatusColor[x.dispoStatus]}>
                    {isVisible ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                  </div>
                </Tooltip>
                <div className="calJobNames">
                  <div className="calJobNumber">{x.phase.project.number}</div>
                  <div className="calJobName">{x.phase.project.name}</div>
                  {Object.keys(x.crews).length > 0 ? (
                    <div className="calCrewName">{Object.values(x.crews).join("; ")}</div>
                  ) : (
                    <div className="calCrewName" />
                  )}
                  <div className="calJobLink">
                    <Button
                      onClick={() => window.open(`/project/${x.phase.project.id}?phaseId=${x.phase.id}&date=${x.date}`, "_blank")}
                      variant="contained"
                      color="info"
                      size="small"
                    >
                      Planung öffnen
                    </Button>
                  </div>
                </div>
              </div>
              {isVisible ? (
                <OrderInformation
                  isTransDispo={"TRANSPORTDISPO" in rights}
                  setModalOpen={setModalOpen}
                  withDirectBooking={"DISPOSITION" in rights}
                  setCalendar={setCalendar}
                  data={x}
                  showInfo={settings.showDispoInformation}
                  showOrders={settings.showDispoOrders}
                  showMaterial={settings.showDispoMaterial}
                  showResources={settings.showDispoResources}
                  showBauhof={settings.showDispoBauhof}
                />
              ) : null}
            </Fragment>
          );
        })
      )}
    </div>
  );
});
